import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Directive, ElementRef, forwardRef, HostListener, Input } from '@angular/core';
import { Platform } from '@angular/cdk/platform';

import { formatNumber, parseNumericValue, roundNumber } from '@kros-sk/shared/utils-fn/number';

@Directive({
  selector: '[krosNumbersOnly]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumbersOnlyDirective),
      multi: true,
    },
  ],
  host: {
    '[attr.type]': '"text"',
    '[attr.inputmode]': '"decimal"',
  },
  standalone: false,
})
export class NumbersOnlyDirective implements ControlValueAccessor {
  readonly allowedDelimiters = [',', '.']; // when adding to allowedDelimiters you need to add char to regExpression too
  readonly allowedChars = ['-']; // when adding to allowedChars you need to add char to regExpression too

  private regExpression = /[0-9,.\-]/gi;
  private regExp = new RegExp(this.regExpression, '');
  #onTouched: () => void = () => {};

  @Input() digitsGrouping = true;
  @Input() minFractionDigits = 2;
  @Input() maxFractionDigits = 6;
  @Input() minValue: number | null = null;
  @Input() maxValue: number | null = null;
  @Input() hideIfNull = false;
  @Input() roundDigits?: number;

  constructor(
    private el: ElementRef,
    private platform: Platform
  ) {}

  @HostListener('keypress', ['$event'])
  onKeyPress(event: any): boolean {
    return this.isCharacterAllowed(event.key) && this.regExp.test(event.key);
  }

  @HostListener('input', ['$event'])
  onInput(_: any): void {}

  @HostListener('blur', ['$event'])
  onBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if ((value === '' || value === null) && this.hideIfNull) {
      return;
    }
    const numberValue = this.checkNumberMinMax(this.parseNumericValue(value.toString()));
    const newValue = roundNumber(numberValue, this.roundDigits ?? this.maxFractionDigits);

    this.reformatValue(newValue);
    this.#onTouched();
  }

  @HostListener('focus')
  onFocus(): void {
    this.el.nativeElement.value = this.el.nativeElement.value.replace(/\s/g, '');
    this.el.nativeElement.select();
  }

  @HostListener('mouseup', ['$event'])
  onmouseup(event: MouseEvent): void {
    // we prevent default behavior for the mouseup event on Safari, because it cancels the text selection
    // made in the focus event
    if (this.platform.SAFARI) event.preventDefault();
  }

  writeValue(value: any): void {
    if ((value === '' || value === null) && this.hideIfNull) {
      this.reformatValue(value);
      return;
    }
    const numberValue = this.checkNumberMinMax(this.parseNumericValue(value?.toString()));
    const newValue = roundNumber(numberValue, this.roundDigits ?? this.maxFractionDigits);
    this.reformatValue(newValue);
  }

  registerOnChange(fn: any): void {
    this.onInput = (event: Event): void => {
      const input = event.target as HTMLInputElement;
      const value = input.value;

      if ((value === '' || value === null) && this.hideIfNull) {
        fn(null);
        return;
      }
      const numberValue = this.checkNumberMinMax(this.parseNumericValue(value?.toString()));
      const newValue = roundNumber(numberValue, this.roundDigits ?? this.maxFractionDigits);
      fn(newValue);
    };
  }

  registerOnTouched(fn: any): void {
    this.#onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  private checkNumberMinMax(value: number): number {
    let newValue = value;
    if (this.minValue || this.minValue === 0) {
      if (this.minValue >= 0 && newValue < 0) {
        newValue = -newValue;
      }
      newValue = Math.max(newValue, this.minValue);
    }
    if (this.maxValue || this.maxValue === 0) {
      newValue = Math.min(newValue, this.maxValue);
    }
    return newValue;
  }

  private isCharacterAllowed(eventKey: string): boolean {
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const elementValue = inputElement.value.toString();

    const selectedText = elementValue.slice(inputElement.selectionStart ?? 0, inputElement.selectionEnd ?? 0);

    if (this.allowedDelimiters.includes(eventKey)) {
      const hasDelimiterOutsideSelection =
        (elementValue.includes(',') && !selectedText.includes(',')) || (elementValue.includes('.') && !selectedText.includes('.'));

      if (hasDelimiterOutsideSelection) {
        return false;
      }
    }

    if (this.allowedChars.includes(eventKey)) {
      const hasCharOutsideSelection = elementValue.includes(eventKey) && !selectedText.includes(eventKey);

      if (hasCharOutsideSelection) {
        return false;
      }
    }

    return true;
  }

  private parseNumericValue(value: string): number {
    if (!/^-?[0-9\s]*,?[0-9\s]*$/i.test(value)) {
      if (!value) {
        value = '0';
      } else {
        const isMinus = value.startsWith('-', 0);
        value = value.replace(/\./gi, ',').replace(/[^0-9,\s]/gi, '');
        if (isMinus) {
          value = '-' + value;
        }
        if (!value) {
          // only text was entered
          value = '0';
        }
      }
    }
    return parseNumericValue(value);
  }

  private reformatValue(value: number): void {
    if ((value?.toString() === '' || value === null) && this.hideIfNull) {
      this.el.nativeElement.value = null;
      return;
    }

    this.el.nativeElement.value = formatNumber(value, this.digitsGrouping, this.minFractionDigits, this.maxFractionDigits);
  }
}
